import { PaletteColorOptions, PaletteOptions, Paper, alpha } from '@mui/material';
import { createTheme, adaptV4Theme, styled, ComponentsVariants } from '@mui/material/styles';
import { ArrowDropDownRounded as ArrowDropDownRoundedIcon, ClearRounded as ClearRoundedIcon } from '@mui/icons-material'

declare module '@mui/material/styles' {
	interface Palette {
		accent: Palette['primary'],
		gray: Palette['primary'],
		reward: Palette['primary'],
		white: Palette['primary']
	}

	interface PaletteOptions {
		accent?: PaletteOptions['primary'],
		gray?: PaletteOptions['primary'],
		reward?: PaletteOptions['primary'],
		white?: PaletteOptions['primary']
	}

	interface TypographyVariants {
		'h1-accent': React.CSSProperties
		'h2-accent': React.CSSProperties
	}

	interface TypographyVariantsOptions {
		'h1-accent'?: React.CSSProperties
		'h2-accent'?: React.CSSProperties
	}

	interface BreakpointOverrides {
		xs: true,
		sm: true,
		md: true,
		lg: true,
		xl: true,
		lab_mobile: true,
		dialog_mobile: true,
	}

	interface Theme {
		height_breakpoints: {
			values: {
				lab_mobile: number
			}
		};
	}

	interface ThemeOptions {
		height_breakpoints?: {
			values?: {
				lab_mobile?: number
			}
		};
	}
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    'h1-accent': true;
		'h2-accent': true;
  }
}

declare module '@mui/material/Button' {
	interface ButtonPropsColorOverrides {
	  accent: true;
	  gray: true;
		white: true;
	}

	interface ButtonPropsVariantOverrides {
		dashed: true;
	}

	interface ButtonPropsSizeOverrides {
		tiny: true;
	}
}

declare module '@mui/material/Paper' {
	interface PaperPropsVariantOverrides {
		dashed: true;
	}
}

declare module '@mui/material/IconButton' {
	interface IconButtonPropsColorOverrides {
	  accent: true;
	  gray: true;
		white: true;
	}
}

declare module '@mui/material/Chip' {
	interface ChipPropsColorOverrides {
	  accent: true;
	  gray: true;
		reward: true;
	}

	interface ChipPropsVariantOverrides {
		badge: true;
		'square-filled': true;
	}
}

declare module '@mui/material/Switch' {
	interface SwitchPropsColorOverrides {
		accent: true
	}
}

declare module '@mui/material/LinearProgress' {
	interface LinearProgressPropsColorOverrides {
		accent: true
	}
}

declare module '@mui/material/Divider' {
	interface DividerPropsVariantOverrides {
		'bold': true;
	}
}

declare module '@mui/material/SvgIcon' {
	interface SvgIconPropsColorOverrides {
		accent: true
		gray: true
		reward: true
	}
}

const h1AccentFontSize = 'clamp(2.5rem, 10vw, 4rem)';

let theme = createTheme({
	typography: {
		fontFamily: [
			'Poppins',
			'Roboto',
			'sans-serif'
		].join(','),
		body1: {
			fontFamily: 'Poppins, Roboto, sans-serif',
		},
		body2: {
			fontSize: '14px',
		},
		subtitle1: {
			textTransform: 'uppercase',
			fontSize: '0.875rem',
			fontWeight: 500,
		},
		caption: {
			color: '#999',
			fontSize: '12px',
			lineHeight: '1.5',
		},
		h1: {
			fontFamily: "'Bebas Neue', sans-serif",
			fontSize: '3.7rem',
			color: '#323232',
			lineHeight: '3.3rem',
		},
		'h1-accent': {
			color: '#f600be',
			fontFamily: 'Rottersand, serif',
			fontSize: h1AccentFontSize,
			// Keep script descenders clear of the title despite the section stacks' negative spacing.
			paddingBottom: `max(1rem, calc(4rem - ${h1AccentFontSize}))`,
			fontStyle: 'normal',
			fontWeight: 'normal',
		},
		h2: {
			fontFamily: 'Bebas Neue, Poppins, sans-serif',
			fontSize: '48px',
			color: '#000',
			lineHeight: '1em',
		},
		'h2-accent': {
			color: '#f600be',
			fontFamily: 'Rottersand, serif',
			fontSize: '2.5rem',
			fontStyle: 'normal',
			fontWeight: 'normal',
		},
		h3: {
			fontSize: '18px',
			textTransform: 'uppercase',
			fontWeight: 600,
		},
		h4: {
			fontSize: '16px',
			lineHeight: 'normal',
			color: '#000',
			fontWeight: 600,
			letterSpacing: 'normal',
		},
		h5: {
			fontSize: '15px',
		}
	},
});

theme = createTheme(theme, {
	palette: {
		primary: {
			main: '#00aced',
			contrastText: '#fff',
		},
		secondary: {
			main: '#000',
			dark: '#333',
			light: '#555',
			contrastText: '#fff',
		},
		accent: theme.palette.augmentColor({
			color: {
				main: '#f600be',
				contrastText: '#fff',
			},
			name: 'accent',
		}),
		gray: theme.palette.augmentColor({
			color: {
				main: '#ebebeb',
			},
			name: 'gray',
		}),
		reward: theme.palette.augmentColor({
			color: {
				main: '#b58a00',
				light: '#fff6cc',
				dark: '#806100',
				contrastText: '#2e2500',
			},
			name: 'reward',
		}),
		white: theme.palette.augmentColor({
			color: {
				main: '#fff',
			},
			name: 'white',
		}),
	},
});

export default createTheme(theme, {
	breakpoints: {
		values: {
			xs: 0,
			sm: 600,
			md: 900,
			lg: 1200,
			xl: 1536,
			lab_mobile: 1053,
			dialog_mobile: 760,
		}
	},
	height_breakpoints: {
		values: {
			lab_mobile: 780,
		}
	},
	shape: {
		borderRadius: 20,
	},
	components: {
		MuiContainer: {
			styleOverrides: {
				root: ({ ownerState, theme }) => ({
					...(!ownerState.disableGutters && {
						[theme.breakpoints.up('sm')]: {
							paddingLeft: theme.spacing(2),
							paddingRight: theme.spacing(2),
						},
						[theme.breakpoints.up('md')]: {
							paddingLeft: theme.spacing(3),
							paddingRight: theme.spacing(3),
						}
					})
				})
			}
		},
		MuiModal: {
			styleOverrides: {
				root: ({ ownerState }) => ({
					pointerEvents: ownerState.open ? undefined : 'none',
				}),
			},
		},
		MuiCssBaseline: {
			styleOverrides: {
				html: {
					fontSize: '15px',
					WebkitFontSmoothing: 'auto',
				},
			}
		},
		MuiButton: {
			variants: [
				{
					props: { variant: 'dashed' },
					style: {
						border: '1px dashed',
					}
				},
				{
					props: { size: 'large' },
					style: {
						padding: '8px 26px',
						//fontSize: '1rem',
						fontSize: '15px',
					}
				},
				{
					props: { size: 'large', variant: 'dashed' },
					style: {
						padding: '7px 25px',
					}
				},
				{
					props: { size: 'large', variant: 'outlined' },
					style: {
						borderWidth: 2,
						padding: '6px 24px',
						'&:hover': {
							borderWidth: 2,
						},
						'&:disabled': {
							borderWidth: 2,
						}
					}
				},
				{
					props: { size: 'medium' },
					style: {
						padding: '6px 16px',
						fontSize: '13px',
					}
				},
				{
					props: { size: 'medium', variant: 'dashed' },
					style: {
						padding: '5px 15px',
					}
				},
				{
					props: { size: 'medium', variant: 'outlined' },
					style: {
						borderWidth: 2,
						padding: '4px 14px',
						'&:hover': {
							borderWidth: 2,
						},
						'&:disabled': {
							borderWidth: 2,
						}
					}
				},
				{
					props: { size: 'small' },
					style: {
						padding: '4px 15px',
						fontSize: '12px',
					}
				},
				{
					props: { size: 'small', variant: 'dashed' },
					style: {
						padding: '3px 14px',
					}
				},
				{
					props: { size: 'small', variant: 'outlined' },
					style: {
						padding: '3px 14px',
					}
				},
				{
					props: { size: 'tiny' },
					style: {
						//fontSize: '0.725rem',
						fontSize: '11px',
						padding: '2px 12px',
						textTransform: 'uppercase',
						'.MuiButton-startIcon > *:nth-of-type(1)': {
							fontSize: '14px'
						}
					}
				},
				{
					props: { size: 'tiny', variant: 'dashed' },
					style: {
						padding: '1px 11px',
					}
				},
				{
					props: { size: 'tiny', variant: 'outlined' },
					style: {
						padding: '1px 11px',
					}
				}
			],
			defaultProps: {
				disableElevation: true
			},
			styleOverrides: {
				root: ({ ownerState, theme, test }) => ({
					borderRadius: 25,
					textTransform: 'none',
					...(ownerState.variant === 'dashed' && {
						borderColor: ownerState.color === 'inherit' ? undefined : alpha(theme.palette[ownerState.color].main, 0.5),
						color: ownerState.color === 'inherit' ? undefined : theme.palette[ownerState.color].main,
						'&:hover': {
							borderColor: ownerState.color === 'inherit' ? undefined : theme.palette[ownerState.color].main,
							backgroundColor: ownerState.color === 'inherit' ? undefined : alpha(theme.palette[ownerState.color].main, 0.04),
						},
						...ownerState.size === 'small' && {
							fontSize: '0.8125rem',
						},
						...ownerState.size === 'medium' && {
							padding: '5px 15px',
							fontSize: '0.875rem',
						},
						...ownerState.size === 'large' && {
							padding: '7px 21px',
							fontSize: '0.9375rem',
						}
					})
				}),
				outlined: ({ ownerState, theme, test }) => ({
					borderColor: ownerState.color === 'inherit' ? undefined : theme.palette[ownerState.color].main,
					'&:hover': {
						backgroundColor: ownerState.color === 'inherit' ? undefined : alpha(theme.palette[ownerState.color].main, 0.15),
					}
				}),
			}
		},
		MuiIconButton: {
			styleOverrides: {
				root: ({ ownerState, theme }) => ({
					...(ownerState.color === 'white' && {
						color: theme.palette.white.main,
						'&:hover': {
							backgroundColor: alpha(theme.palette.white.main, theme.palette.action.hoverOpacity),
						},
					}),
				}),
			},
			variants: [
				{
					props: { size: 'large' },
					style: {
						fontSize: '26px',
						'> .MuiSvgIcon-fontSizeMedium': {
							fontSize: '22px',
						},
						'> .MuiSvgIcon-fontSizeLarge': {
							fontSize: '26px',
						},
					}
				},
				{
					props: { size: 'medium' },
					style: {
						fontSize: '22px',
						'> .MuiSvgIcon-fontSizeMedium': {
							fontSize: '22px',
						},
						'> .MuiSvgIcon-fontSizeLarge': {
							fontSize: '26px',
						},
					}
				},
			]
		},
		MuiTabs: {
			styleOverrides: {
				root: {
					[theme.breakpoints.down('sm')]: {
						minHeight: '36px',
					}
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					fontSize: '1rem',
					minWidth: 0,

					[theme.breakpoints.down('sm')]: {
						fontSize: '0.875rem',
						minHeight: '36px',
					}
				},
			},
		},
		MuiCard: {
			variants: [
				{
					props: { variant: 'dashed' },
					style: ({ theme }) => ({
						backgroundColor: 'rgba(255, 255, 255, 0.65)',
						border: '2px dashed',
						borderColor: theme.palette.divider,
						boxShadow: 'none',
					}),
				},
			],
		},
		MuiCardContent: {
			styleOverrides: {
				root: {
					padding: 24,
				}
			}
		},
		MuiCardActions: {
			styleOverrides: {
				root: {
					background: 'rgba(0, 0, 0, 0.06)',
					justifyContent: 'space-between',
    				padding: 0,
				}
			}
		},
		MuiChip: {
			variants: [
				{
					props: { variant: 'square-filled' },
					style: {
						borderRadius: 0,
					}
				},
				{
					props: { size: 'small' },
					style: {
						height: 'auto',
						lineHeight: '1.75',
						fontSize: '0.725rem',
						padding: '2px 0',
						textTransform: 'uppercase',
					}
				},
				{
					props: { variant: 'badge' },
					style: {
						alignItems: 'center',
						backgroundColor: alpha(theme.palette.common.black, 0.04),
						border: '1px solid ' + alpha(theme.palette.common.black, 0.07),
						borderRadius: 999,
						boxSizing: 'border-box',
						color: alpha(theme.palette.text.primary, 0.72),
						display: 'inline-flex',
						fontSize: 12,
						fontWeight: 600,
						height: 23,
						letterSpacing: 0,
						lineHeight: 1,
						padding: 0,
						textTransform: 'none',
						whiteSpace: 'nowrap',

						'& .MuiChip-label': {
							display: 'block',
							lineHeight: 1.2,
							minWidth: 0,
							overflow: 'hidden',
							paddingLeft: theme.spacing(1),
							paddingRight: theme.spacing(1),
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						},

						'& .MuiChip-icon': {
							color: 'currentColor',
							flexShrink: 0,
							fontSize: 15,
							marginLeft: theme.spacing(0.75),
							marginRight: theme.spacing(-0.25),
						},
					}
				},
				{
					props: { variant: 'badge', size: 'small' },
					style: {
						fontSize: 11,
						height: 19,

						'& .MuiChip-label': {
							paddingLeft: theme.spacing(0.625),
							paddingRight: theme.spacing(0.625),
						},

						'& .MuiChip-icon': {
							fontSize: 14,
							marginLeft: theme.spacing(0.625),
						},
					}
				},
				{
					props: { variant: 'badge', color: 'primary' },
					style: {
						backgroundColor: alpha(theme.palette.common.white, 0.92),
						borderColor: alpha(theme.palette.primary.main, 0.28),
						color: theme.palette.primary.main,
					}
				},
				{
					props: { variant: 'badge', color: 'secondary' },
					style: {
						backgroundColor: alpha(theme.palette.common.white, 0.92),
						borderColor: alpha(theme.palette.secondary.main, 0.28),
						color: theme.palette.secondary.main,
					}
				},
				{
					props: { variant: 'badge', color: 'accent' },
					style: {
						backgroundColor: alpha(theme.palette.common.white, 0.92),
						borderColor: alpha(theme.palette.accent.main, 0.28),
						color: theme.palette.accent.main,
					}
				},
				{
					props: { variant: 'badge', color: 'gray' },
					style: {
						backgroundColor: theme.palette.gray.main,
						borderColor: alpha(theme.palette.text.secondary, 0.22),
						color: theme.palette.text.secondary,
					}
				},
				{
					props: { variant: 'badge', color: 'success' },
					style: {
						backgroundColor: alpha(theme.palette.success.main, 0.09),
						borderColor: alpha(theme.palette.success.main, 0.24),
						color: theme.palette.success.main,
					}
				},
				{
					props: { variant: 'badge', color: 'info' },
					style: {
						backgroundColor: alpha(theme.palette.info.main, 0.09),
						borderColor: alpha(theme.palette.info.main, 0.24),
						color: theme.palette.info.main,
					}
				},
				{
					props: { variant: 'badge', color: 'warning' },
					style: {
						backgroundColor: alpha(theme.palette.warning.main, 0.09),
						borderColor: alpha(theme.palette.warning.main, 0.24),
						color: theme.palette.warning.main,
					}
				},
				{
					props: { variant: 'badge', color: 'reward' },
					style: {
						backgroundColor: theme.palette.reward.light,
						borderColor: alpha(theme.palette.reward.main, 0.42),
						color: theme.palette.reward.dark,
					}
				}
			],
			styleOverrides: {
				root: {
					lineHeight: 'normal'
				}
			}
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					backgroundColor: 'rgba(0, 0, 0, 0.8)',
					color: '#fff',
					fontWeight: 400,
					backdropFilter: 'blur(10px)',

					'&.MuiTooltip-variantMultiline': {
						lineHeight: 1.45,
						maxWidth: 320,
						padding: '10px 12px',
					},
				},
				arrow: {
					color: 'rgba(0, 0, 0, 0.8)',
				},
			},
		},
		MuiAlert: {
			variants: [
				{
					props: { variant: 'standard' },
					style: {
						borderRadius: 10,
					}
				},
				{
					props: { variant: 'outlined', severity: 'info' },
					style: {
						backgroundColor: '#e5f6fd',
					}
				},
				{
					props: { variant: 'outlined', severity: 'warning' },
					style: {
						backgroundColor: '#fff4e5',
					}
				},
				{
					props: { variant: 'outlined', severity: 'success' },
					style: {
						backgroundColor: '#edf7ed',
					}
				},
				{
					props: { variant: 'outlined', severity: 'error' },
					style: {
						backgroundColor: '#fdeded',
					}
				}
			]
		},
		MuiFilledInput: {
			styleOverrides: {
				root: {
					borderRadius: 50,
					'&::after': {
						display: 'none',
					},
					'&::before': {
						display: 'none',
					},
				},
				input: {
					padding: '25px 20px 8px',
				},
			}
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					'&:not(.MuiInputLabel-formControl)': {
						fontSize: '.9rem',
						fontWeight: 500,
						lineHeight: 1.2,
						marginBottom: '5px',
					},
				},
				filled: ({ ownerState }) => ({
				...(ownerState.shrink ? {
						transform: 'translate(20px, 7px) scale(0.75)',
					} : {
						transform: 'translate(20px, 16px) scale(1)',
					}),
				})
			}
		},
		MuiFormLabel: {
			styleOverrides: {
				root: {
					'&:not(.MuiInputLabel-formControl)': {
						fontSize: '.9rem',
						fontWeight: 500,
						lineHeight: 1.2,
					},
				},
			},
		},
		MuiTextField: {
			defaultProps: {
				variant: 'standard',
				fullWidth: true,
			},
			variants: [
				{
					props: { variant: 'standard' },
					style: {
						'.MuiInputLabel-root': {
							fontSize: '.9rem',
							fontWeight: 500,
							lineHeight: 1.2,
							transform: 'none',
							marginBottom: '5px',
							position: 'static',
						},
						'label + .MuiInputBase-root': {
							marginTop: 0,
						}
					}
				}
			],
		},
		MuiInput: {
			defaultProps: {
				disableUnderline: true,
			},
			styleOverrides: {
				root: {
					backgroundColor: 'rgba(0, 0, 0, 0.06)',
					borderRadius: '30px',
					overflow: 'hidden',
					transition: 'background-color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms',
					padding: 0,

					'&.MuiInputBase-sizeSmall': {
						'.MuiInput-input': {
							padding: '7px 16px',
						},
					},

					'&.MuiInputBase-adornedStart': {
						'.MuiInputAdornment-positionStart': {
							alignSelf: 'center',
							marginLeft: '16px',
							marginRight: '8px',
						},
						'.MuiInput-input': {
							paddingLeft: 0,
							borderTopLeftRadius: 0,
							borderBottomLeftRadius: 0,
						},
					},

					'&.MuiInputBase-adornedEnd': {
						'.MuiInputAdornment-positionEnd:not(:has(.MuiButton-root))': {
							marginLeft: '8px',
							marginRight: '16px',
						},
					},

					'&:not(.Mui-disabled)': {
						'&:hover, &:focus-within': {
							backgroundColor: 'rgba(0, 0, 0, 0.12)',
						},
						'&:has(input:-webkit-autofill)': {
							backgroundColor: '#e8f0fe',
						},
						'&:has(input:-webkit-autofill):hover, &:has(input:-webkit-autofill):focus-within': {
							backgroundColor: '#dce8fb',
						}
					}
				},
				input: {
					//borderRadius: 'inherit',
					borderRadius: 'inherit',
					padding: '10px 16px',
					'&:-webkit-autofill': {
						WebkitBoxShadow: '0 0 0 100px #e8f0fe inset',
						WebkitTextFillColor: 'inherit',
						caretColor: 'inherit',
					},
				}
			}
		},
		MuiAutocomplete: {
			defaultProps: {
				componentsProps: {
          paper: {
            elevation: 8,
          }
        },
				popupIcon: <ArrowDropDownRoundedIcon/>,
				clearIcon: <ClearRoundedIcon fontSize="small"/>
			},
			styleOverrides: {
				listbox: {
					'&::-webkit-scrollbar': {
						width: '10px',
						height: '10px'
					},
					
					'&::-webkit-scrollbar-track': {
						backgroundColor: 'transparent'
					},
					
					'&::-webkit-scrollbar-thumb': {
						backgroundColor: 'rgba(0, 0, 0, 0.15)',
						borderRadius: '10px',
					
						'&:hover': {
						  backgroundColor: 'rgba(0, 0, 0, 0.25)'
						}
					}
				},
				inputRoot: {
					padding: 0,
					'.MuiInput-input': {
						padding: '10px 16px 10px 16px',
					}
				},
				endAdornment: {
					right: '16px',
				}
			}
		},
		MuiFormControlLabel: {
			styleOverrides: {
				root: {
					alignItems: 'flex-start',

					'.MuiRadio-root + .MuiFormControlLabel-label, .MuiCheckbox-root + .MuiFormControlLabel-label': {
						paddingTop: '9.5px',
					},
					'.MuiSwitch-root + .MuiFormControlLabel-label': {
						paddingTop: '8px',
					}
				},
			}
		},
		MuiRadio: {
			defaultProps: {
				color: 'secondary'
			}
		},
		MuiSwitch: {
			variants: [
				{
					props: { color: 'accent' },
					style: {
						'& .MuiSwitch-switchBase.Mui-checked': {
							color: theme.palette.accent.main,
							'&:hover': {
							  backgroundColor: alpha(theme.palette.accent.main, theme.palette.action.hoverOpacity),
							},
						},
						'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
							backgroundColor: theme.palette.accent.main,
						},
					}
				}
			]
		},
		MuiSelect: {
			defaultProps: {
				IconComponent: ArrowDropDownRoundedIcon
			}
		},
		/*MuiSelect: {
			defaultProps: {
				variant: 'standard'
			},
			styleOverrides: {
				inputRoot: {
					paddingTop: '10px',
					paddingBottom: '10px',
					'.MuiInput-input': {
						//padding: '10px 20px',
						padding: 0,
					}
				},
				icon: {
					right: '16px',
				}
			}
		},*/
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					//marginTop: 0,
					//marginBottom: '-16px',
					//lineHeight: '16px',
					'.MuiRadioGroup-root + &': {
						marginLeft: 0,
						marginRight: 0,
					},
				}
			}
		},
		MuiDivider: {
			variants: [
				{
					props: { variant: 'bold' },
					style: {
						//borderBottomWidth: '2px',
						borderColor: 'rgba(0, 0, 0, 0.4)'
					}
				},
			]
		},
		MuiDialog: {
			variants: [
				{
					props: { maxWidth: 'lg' },
					style: {
						'.MuiDialogTitle-root': {
							padding: '32px 60px 0 32px',

							[theme.breakpoints.down('sm')]: {
								padding: '16px 60px 0 24px',
							},
						},
						'.MuiDialogContent-root': {
							padding: '32px',
							
							[theme.breakpoints.down('sm')]: {
								padding: '20px 24px',
							}
						},
						'.MuiDialogTitle-root+.MuiDialogContent-root': {
							paddingTop: 0,
						}
					}
				},
				{
					props: { maxWidth: 'md' },
					style: {
						'.MuiDialogTitle-root': {
							padding: '32px 60px 0 40px',
							
							[theme.breakpoints.down('sm')]: {
								padding: '16px 60px 0 24px',
							},
						},
						'.MuiDialogContent-root': {
							padding: '40px',
							
							[theme.breakpoints.down('sm')]: {
								padding: '20px 24px',
							}
						},
						'.MuiDialogTitle-root+.MuiDialogContent-root': {
							paddingTop: 0,
						}
					}
				}	
			],
			styleOverrides: {
					
				paper: {
					//borderRadius: 30
				}
			}
		},
		MuiDialogContent: {
			styleOverrides: {
				root: {
					//padding: '40px',
				}
			}
		},
		MuiDialogTitle: {
			styleOverrides: {
				root: {
					fontFamily: 'Bebas Neue',
					fontSize: '32px',
					lineHeight: 'normal',
					padding: '16px 24px 0',
				}
			}
		},
		MuiListItemIcon: {
			styleOverrides: {
				root: {
					color: '#000',
				}
			}
		},
		MuiLinearProgress: {
			styleOverrides: {
				root: {
					height: 10,
					borderRadius: 20,
				}
			}
		}
	}
});
